import {
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import { useState } from 'react';
import { SearchableDropdown } from './searchable-dropdown';
import type { DropdownOption } from './searchable-dropdown';

interface CheckboxTextInputProps {
  checkboxLabel: string;
  checkboxHint?: string;
  dropdownOptions: DropdownOption[];
  dropdownValue: string | null;
  dropdownPlaceholder?: string;
  textValue: string;
  textPlaceholder?: string;
  dropdownDisabled: boolean;
  textDisabled: boolean;
  disabled?: boolean;
  /** When true, the checkbox itself is disabled (cannot be toggled). */
  checkboxDisabled?: boolean;
  error?: string;
  onToggle: (checked: boolean) => void;
  onDropdownChange: (value: string | null) => void;
  onTextChange: (value: string) => void;
  onDropdownBlur?: () => void;
  onTextBlur?: () => void;
}

export function CheckboxTextInput({
  checkboxLabel,
  checkboxHint,
  dropdownOptions,
  dropdownValue,
  dropdownPlaceholder = 'Chọn...',
  textValue,
  textPlaceholder = 'Nhập mô tả...',
  dropdownDisabled,
  textDisabled,
  disabled = false,
  checkboxDisabled = false,
  error,
  onToggle,
  onDropdownChange,
  onTextChange,
  onDropdownBlur,
  onTextBlur,
}: CheckboxTextInputProps) {
  const [showHint, setShowHint] = useState(false);

  const handleDisabledTextClick = () => {
    if (textDisabled && !disabled) {
      setShowHint(true);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={textDisabled === false}
            disabled={disabled || checkboxDisabled}
            onChange={(e) => onToggle(e.target.checked)}
            sx={{
              color: 'text.secondary',
              '&.Mui-checked': {
                color: '#00A651',
              },
            }}
          />
        }
        label={
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {checkboxLabel}
            </Typography>
            {checkboxHint && (
              <Typography variant="caption" color="text.secondary">
                {checkboxHint}
              </Typography>
            )}
          </Box>
        }
        sx={{ mb: 1 }}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          sx={{
            opacity: disabled || dropdownDisabled ? 0.4 : 1.0,
            transition: 'opacity 200ms ease-in-out, background-color 200ms ease-in-out',
            pointerEvents: disabled || dropdownDisabled ? 'none' : 'auto',
            backgroundColor: disabled || dropdownDisabled ? 'action.disabledBackground' : 'transparent',
            borderRadius: 1,
            p: disabled || dropdownDisabled ? 1 : 0,
          }}
        >
          <SearchableDropdown
            options={dropdownOptions}
            value={dropdownValue}
            onChange={onDropdownChange}
            onBlur={onDropdownBlur}
            placeholder={dropdownPlaceholder}
            disabled={disabled || dropdownDisabled}
            error={!!error && !dropdownDisabled}
          />
        </Box>

        <Box
          onClick={handleDisabledTextClick}
          sx={{
            opacity: disabled || textDisabled ? 0.4 : 1.0,
            transition: 'opacity 200ms ease-in-out, background-color 200ms ease-in-out',
            cursor: textDisabled && !disabled ? 'pointer' : undefined,
            backgroundColor: disabled || textDisabled ? 'action.disabledBackground' : 'background.paper',
            borderRadius: 1,
            p: disabled || textDisabled ? 1 : 0,
          }}
        >
          <TextField
            fullWidth
            value={textValue}
            onChange={(e) => onTextChange(e.target.value)}
            onBlur={onTextBlur}
            placeholder={textPlaceholder}
            disabled={disabled || textDisabled}
            error={!!error && !textDisabled}
            multiline
            rows={2}
          />
        </Box>
      </Box>

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {error}
        </Typography>
      )}

      <Snackbar
        open={showHint}
        autoHideDuration={3000}
        onClose={() => setShowHint(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowHint(false)}
          severity="info"
          sx={{ width: '100%' }}
        >
          Vui lòng tick vào ô bên trên để nhập thủ công
        </Alert>
      </Snackbar>
    </Box>
  );
}
